import React, { useState, useMemo } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceArea, ResponsiveContainer } from 'recharts';
import { ChannelBreakdownDonut, ConversionsByChannelBar } from '../ChartsSection';
import { fmtNumber, fmtPercent, fmtDuration } from '../shared/format';
import {
  sortRows, downloadCsv, downloadPdf, makeExport,
  CHANNEL_COLORS, TREND_METRICS,
  SortableHeader, Sparkline, TableSkeleton, SectionInsightCard, ViewToggle,
} from '../shared';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import { fmtTrendDate, dateToTs, findTrendGaps } from '../../shared/trendAxis';
import { expandableRowProps } from '../../shared/expandableRow';

/* ============================= Growth badges ============================= */
const computeChannelGrowth = (trendData) => {
  if (!trendData || trendData.length < 4) return [];
  const byChannel = {};
  for (const row of trendData) {
    if (!byChannel[row.channel]) byChannel[row.channel] = [];
    byChannel[row.channel].push({ date: row.date, sessions: row.sessions || 0 });
  }
  const badges = [];
  for (const [channel, days] of Object.entries(byChannel)) {
    if (days.length < 4) continue;
    days.sort((a, b) => a.date.localeCompare(b.date));
    const mid = Math.floor(days.length / 2);
    const firstHalf = days.slice(0, mid).reduce((s, d) => s + d.sessions, 0);
    const secondHalf = days.slice(mid).reduce((s, d) => s + d.sessions, 0);
    if (firstHalf === 0 && secondHalf === 0) continue;
    const pctChange = firstHalf > 0
      ? ((secondHalf - firstHalf) / firstHalf) * 100
      : secondHalf > 0 ? 100 : 0;
    if (pctChange >= 10) {
      badges.push({ channel, pctChange: Math.round(pctChange) });
    }
  }
  return badges.sort((a, b) => b.pctChange - a.pctChange).slice(0, 5);
};

const ChannelBadges = ({ trendData }) => {
  const badges = useMemo(() => computeChannelGrowth(trendData), [trendData]);
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-4">
      {badges.map((badge) => (
        <span
          key={badge.channel}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
        >
          {badge.channel} ↑ {badge.pctChange}%
        </span>
      ))}
    </div>
  );
};

/* ============================= Per-channel daily trend (drill-down) ============================= */
const ChannelDrillDown = ({ channel, trendData }) => {
  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];
    return trendData
      .filter(row => row.channel === channel)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(row => ({
        ts: dateToTs(row.date),
        dateLabel: fmtTrendDate(row.date),
        sessions: row.sessions || 0,
        users: row.users || 0,
        conversions: row.conversions || 0,
      }));
  }, [trendData, channel]);

  /* findTrendGaps returns { x1: ts, x2: ts, label }. We need to map those
     timestamps to the dateLabel strings that our category axis uses. */
  const rawGaps = useMemo(() => findTrendGaps(chartData), [chartData]);
  const tsToLabel = useMemo(
    () => Object.fromEntries(chartData.map(d => [String(d.ts), d.dateLabel])),
    [chartData],
  );
  const gaps = useMemo(
    () => rawGaps
      .map(g => ({
        ...g,
        x1Label: tsToLabel[String(g.x1)],
        x2Label: tsToLabel[String(g.x2)],
      }))
      .filter(g => g.x1Label && g.x2Label),
    [rawGaps, tsToLabel],
  );

  if (chartData.length === 0) {
    return <div className="text-xs text-gray-400 py-6 text-center">No daily trend data for {channel}.</div>;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
        {channel} — Daily Trend
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        {/* FIX v2: Switched to a category axis (dataKey="dateLabel") so recharts
            auto-sizes the Y-axis to fit labels — no more clipping. Margins are
            modest because the axis widths are now automatic. */}
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          {gaps.map((gap) => (
            <ReferenceArea
              key={gap.x1Label}
              x1={gap.x1Label}
              x2={gap.x2Label}
              fill="#9ca3af"
              fillOpacity={0.12}
              ifOverflow="extendDomain"
            >
              <title>{gap.label}</title>
            </ReferenceArea>
          ))}
          {/* Category axis: dateLabel values positioned at each bar. interval={1}
              shows every other label; rotation prevents overlap. height gives the
              rotated labels vertical room. */}
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 9, fill: '#9ca3af' }}
            interval={1}
            angle={-35}
            textAnchor="end"
            height={40}
          />
          {/* No explicit width — recharts auto-sizes to fit tick labels. */}
          <YAxis
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <ThemedChartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <ThemedTooltipBox title={d.dateLabel}>
                  <div>Sessions: {d.sessions.toLocaleString()}</div>
                  <div>Users: {d.users.toLocaleString()}</div>
                  <div>Conversions: {d.conversions.toLocaleString()}</div>
                </ThemedTooltipBox>
              );
            }}
          />
          <Bar dataKey="sessions" fill="#32B1FF" radius={[2, 2, 0, 0]} maxBarSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ============================= Channels table ============================= */
const ChannelsTable = ({ data, trendData, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const [expandedChannel, setExpandedChannel] = useState(null);

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'desc' }
    );
  };

  const sparklines = useMemo(() => {
    if (!trendData || trendData.length === 0) return {};
    const map = {};
    for (const row of trendData) {
      if (!map[row.channel]) map[row.channel] = [];
      map[row.channel].push(row.sessions);
    }
    return map;
  }, [trendData]);

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const q = searchQuery.toLowerCase();
    return (data || []).filter(row => (row.channel || '').toLowerCase().includes(q));
  }, [data, searchQuery]);

  const totalSessions = useMemo(
    () => filtered.reduce((s, r) => s + (r.sessions || 0), 0),
    [filtered],
  );

  const enriched = useMemo(
    () => filtered.map(row => ({
      ...row,
      pct_of_total: totalSessions > 0 ? (row.sessions / totalSessions) * 100 : 0,
    })),
    [filtered, totalSessions],
  );

  const sorted = useMemo(() => sortRows(enriched, sort.key, sort.dir), [enriched, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No channel data for this period." padding="py-12" />;
  }

  return (
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Channel" sortKey="channel" currentSort={sort} onSort={handleSort} />
            <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Trend</th>
            <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="% of Total" sortKey="pct_of_total" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Avg. Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <React.Fragment key={row.channel}>
              <tr
                {...expandableRowProps({
                  expanded: expandedChannel === row.channel,
                  onToggle: () => setExpandedChannel(expandedChannel === row.channel ? null : row.channel),
                  label: `details for ${row.channel}`,
                  className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${
                    expandedChannel === row.channel ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'rc-collapsed'
                  }`,
                })}
              >
                <td
                  data-label="Channel"
                  className={`rc-title-cell px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap ${
                    expandedChannel === row.channel ? 'rc-title-open' : ''
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <svg
                      className={`w-3 h-3 text-gray-400 transition-transform ${expandedChannel === row.channel ? 'rotate-90' : ''}`}
                      viewBox="0 0 12 12"
                    >
                      <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    {row.channel}
                  </span>
                </td>
                <td data-label="Trend" className="px-3 py-2.5"><Sparkline values={sparklines[row.channel]} /></td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                <td data-label="% of Total" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{row.pct_of_total.toFixed(1)}%</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtDuration(row.avg_session_duration)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
              </tr>
              {expandedChannel === row.channel && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b dark:border-gray-800">
                    <ChannelDrillDown channel={row.channel} trendData={trendData} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ============================= Stacked channel trend chart ============================= */
const aggregateChannelByWeek = (rows) => {
  const weeks = {};
  for (const row of rows) {
    const date = new Date(row.date + 'T00:00:00');
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(date);
    weekStart.setDate(diff);
    const key = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
    const weekKey = `${key}|${row.channel}`;
    if (!weeks[weekKey]) weeks[weekKey] = { date: key, channel: row.channel, sessions: 0, users: 0, conversions: 0 };
    weeks[weekKey].sessions += row.sessions || 0;
    weeks[weekKey].users += row.users || 0;
    weeks[weekKey].conversions += row.conversions || 0;
  }
  return Object.values(weeks);
};

const ChannelTrendChart = ({ trendData, channelData, loading, error }) => {
  const [metric, setMetric] = useState('sessions');

  const dates = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];
    return [...new Set(trendData.map(r => r.date))].sort();
  }, [trendData]);

  const useWeekly = dates.length > 60;

  const topChannels = useMemo(() => {
    if (!channelData || channelData.length === 0) return [];
    return [...channelData]
      .sort((a, b) => (b.sessions || 0) - (a.sessions || 0))
      .slice(0, 8)
      .map(r => r.channel);
  }, [channelData]);

  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0 || topChannels.length === 0) return [];
    const rows = useWeekly ? aggregateChannelByWeek(trendData) : trendData;
    const byDate = {};
    for (const row of rows) {
      if (!topChannels.includes(row.channel)) continue;
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date };
        for (const ch of topChannels) byDate[row.date][ch] = 0;
      }
      byDate[row.date][row.channel] = (byDate[row.date][row.channel] || 0) + (row[metric] || 0);
    }
    return Object.values(byDate)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(row => ({
        ...row,
        label: new Date(row.date + 'T00:00:00').toLocaleDateString(
          'en-US',
          useWeekly ? { month: 'short', day: 'numeric' } : { weekday: 'short', month: 'short', day: 'numeric' },
        ),
      }));
  }, [trendData, topChannels, metric, useWeekly]);

  const distinctWeeks = useMemo(() => {
    if (dates.length === 0) return 0;
    const weekSet = new Set();
    for (const date of dates) {
      const d = new Date(date + 'T00:00:00');
      const year = d.getFullYear();
      const jan1 = new Date(year, 0, 1);
      const weekNum = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
      weekSet.add(`${year}-W${weekNum}`);
    }
    return weekSet.size;
  }, [dates]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <SkeletonChart height={280} />;
  if (!trendData || trendData.length === 0) {
    return <EmptyState message="No channel trend data for this period." padding="py-12" />;
  }
  if (distinctWeeks < 2) {
    return (
      <EmptyState message="Trend data will be available once you have multiple weeks of analytics data." padding="py-12" />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        {TREND_METRICS.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMetric(m.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              metric === m.key
                ? 'text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            style={metric === m.key ? { backgroundColor: m.color } : undefined}
          >
            {m.label}
          </button>
        ))}
        {useWeekly && (
          <span className="ml-3 text-[11px] text-gray-400 dark:text-gray-500 italic">Aggregated by week</span>
        )}
      </div>

      {/* Removed rotate-x-labels class — XAxis already handles its own
          angle/textAnchor/height props, so the class was redundant. */}
      <div>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }} stackOffset="none">
            <defs>
              {topChannels.map((ch, i) => (
                <linearGradient key={ch} id={`channelFill-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
              width={50}
            />
            <ThemedChartTooltip
              formatter={(value, name) => [Number(value).toLocaleString(), name]}
              labelFormatter={(label) => label}
            />
            {topChannels.map((ch, i) => (
              <Area
                key={ch}
                type="monotone"
                dataKey={ch}
                name={ch}
                stackId="channels"
                stroke={CHANNEL_COLORS[i % CHANNEL_COLORS.length]}
                fill={`url(#channelFill-${i})`}
                strokeWidth={1.5}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3 px-2">
        {topChannels.map((ch, i) => (
          <div key={ch} className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
            <span className="text-xs text-gray-600 dark:text-gray-400">{ch}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getChannelSummary = (data) => {
  if (!data || data.length === 0) return { summary: [], warnings: [] };
  const summary = [];
  const warnings = [];
  const sorted = [...data].sort((a, b) => (b.sessions || 0) - (a.sessions || 0));
  const total = data.reduce((s, r) => s + (r.sessions || 0), 0);
  if (sorted[0] && total > 0) {
    const pct = ((sorted[0].sessions / total) * 100).toFixed(1);
    summary.push(`${sorted[0].channel} is the top channel, driving ${pct}% of all sessions (${sorted[0].sessions.toLocaleString()}).`);
  }
  const highBounce = sorted.find(r => r.sessions > total * 0.05 && (r.bounce_rate || 0) > 0.6);
  if (highBounce) {
    summary.push(`${highBounce.channel} has a ${(highBounce.bounce_rate * 100).toFixed(1)}% bounce rate.`);
    warnings.push({
      color: 'amber',
      text: `${highBounce.channel} bounce rate at ${(highBounce.bounce_rate * 100).toFixed(1)}% — landing pages may need optimization`,
    });
  }
  return { summary, warnings };
};

/* ============================= Export helpers ============================= */
const CHANNELS_EXPORT = makeExport([
  { header: 'Channel', get: (r) => r.channel },
  { header: 'Sessions', get: (r) => r.sessions, pdf: (r) => fmtNumber(r.sessions) },
  { header: 'Users', get: (r) => r.users, pdf: (r) => fmtNumber(r.users) },
  { header: 'New Users', get: (r) => r.new_users, pdf: (r) => fmtNumber(r.new_users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (r) => (r.bounce_rate != null ? (r.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (r) => fmtPercent(r.bounce_rate) },
  { header: 'Avg Duration', pdfHeader: 'Dur.', get: (r) => r.avg_session_duration, pdf: (r) => fmtDuration(r.avg_session_duration) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (r) => r.conversions, pdf: (r) => fmtNumber(r.conversions) },
]);

export const exportChannelsCsv = (channelData, rangeStart, rangeEnd) => {
  if (!channelData?.length) return;
  downloadCsv(CHANNELS_EXPORT.headers, channelData.map(CHANNELS_EXPORT.toCsvRow), `ga4-channels-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportChannelsPdf = (channelData, rangeStart, rangeEnd) => {
  if (!channelData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Channels', CHANNELS_EXPORT.pdfHeaders, channelData.map(CHANNELS_EXPORT.toPdfRow), `ga4-channels-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */
const ChannelsTab = ({ channelData, channelLoading, channelError, channelTrendData, channelTrendError, searchQuery }) => {
  const [view, setView] = useState('table');

  return (
    <div>
      <SectionInsightCard title="Channels Insight" {...getChannelSummary(channelData)} />
      <ChannelBadges trendData={channelTrendData} />
      <ViewToggle view={view} onChange={setView} />
      {view === 'table' ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChannelBreakdownDonut data={channelData} loading={channelLoading} />
            <ConversionsByChannelBar data={channelData} loading={channelLoading} />
          </div>
          <ChannelsTable data={channelData} trendData={channelTrendData} loading={channelLoading} error={channelError} searchQuery={searchQuery} />
        </>
      ) : (
        <ChannelTrendChart trendData={channelTrendData} channelData={channelData} loading={channelLoading} error={channelTrendError} />
      )}
    </div>
  );
};

export default ChannelsTab;
